import React, { useEffect, useState } from 'react';
import { BsPencil } from 'react-icons/bs';
import { FaXmark } from 'react-icons/fa6';
import { Button } from 'react-bootstrap';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../../../../../shared/functions/Functions';

interface IHeaderProps {
    createdAt: string;
    offerItemId: number;
    status?: string;
    isRequested?: boolean;
    isApproved?: boolean;
    isDeclined?: boolean;
    offerModalStatus?: string;
    isEditable?: boolean;
    acceptedDate?: string;
    purchaseOrderInfluencer?: string;
    onSavePurchaseOrder?: (value: string) => Promise<boolean>;
}

export function OfferHeader({
    createdAt,
    offerItemId,
    isRequested,
    isApproved,
    isDeclined,
    isEditable,
    offerModalStatus,
    acceptedDate,
    status,
    purchaseOrderInfluencer,
    onSavePurchaseOrder,
}: IHeaderProps) {
    const { t } = useTranslation();
    const isAccepted = status === 'accepted' || status === 'completed';
    const [po, setPo] = useState(purchaseOrderInfluencer ?? '');
    const [isEditingPo, setIsEditingPo] = useState(false);
    const [draft, setDraft] = useState('');

    useEffect(() => {
        setPo(purchaseOrderInfluencer ?? '');
    }, [purchaseOrderInfluencer]);

    const savePo = async () => {
        const value = draft.trim();
        if (onSavePurchaseOrder && (await onSavePurchaseOrder(value))) {
            setPo(value);
            setIsEditingPo(false);
        }
    };

    return (
        <div className="d-flex justify-content-between">
            <p className="text-muted mb-0">
                {offerItemId ? 'Offer ID: #' + offerItemId : ''}
                {isAccepted && (
                    <>
                        {' - '}
                        {isEditingPo ? (
                            <span className="d-inline-flex align-items-center">
                                <span className="mr-2">PO:</span>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter PO"
                                    value={draft}
                                    autoFocus
                                    onChange={e => setDraft(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            savePo();
                                        }
                                        if (e.key === 'Escape') setIsEditingPo(false);
                                    }}
                                />
                                <Button
                                    className="mx-2"
                                    variant="outline-secondary"
                                    onClick={() => setIsEditingPo(false)}
                                >
                                    <FaXmark className="text-danger" size={18} />
                                </Button>
                                <Button variant="outline-secondary" onClick={savePo}>
                                    <img width={15} alt="Confirm icon" src="/icons/img-switch-check.png" />
                                </Button>
                            </span>
                        ) : (
                            <>
                                <span className="text-primary">PO: {po || '-'}</span>
                                {onSavePurchaseOrder && (
                                    <BsPencil
                                        className="ml-2 text-primary"
                                        role="button"
                                        onClick={() => {
                                            setDraft(po);
                                            setIsEditingPo(true);
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </p>

            <div className="d-flex justify-content-center">
                <div>
                    {isRequested && (
                        <p className="mr-2 status invite text-dark">{t(`general.campaign_status.invite`)}</p>
                    )}
                    {isApproved && (
                        <p className="mr-2 status proposal-approved text-dark">
                            {t(`brand.campaign.book.offer.approved`)}
                        </p>
                    )}
                    {isDeclined && (
                        <p className="mr-2 status proposal-declined text-dark">
                            {t(`brand.campaign.book.offer.declined`)}
                        </p>
                    )}
                </div>
                {offerModalStatus !== 'negotiate' && (
                    <p className="text-muted text-end mb-0">
                        {isAccepted && acceptedDate ? (
                            <>
                                {t('brand.campaign.book.offer.modal.accepted_at')} – {formatDate(acceptedDate)} –{' '}
                                {acceptedDate.substring(11, 16)}
                            </>
                        ) : (
                            <>
                                {isEditable
                                    ? t('brand.campaign.book.offer.modal.received_at')
                                    : t('brand.campaign.book.offer.modal.sent_at')}
                                {' – '}
                                {formatDate(createdAt)} – {createdAt.substring(11, 16)}
                            </>
                        )}
                    </p>
                )}
            </div>
        </div>
    );
}
